import { CartLinesQueryResult, useCartLineKitContentsLazyQuery } from '@graphql/queries/__generated__/cart.generated'
import { Collapse, Divider, IconButton, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import useMediaQuery from '@mui/material/useMediaQuery'
import React, { useEffect, useRef, useState } from 'react'
import Loader from '@components/Loader'
import { Link } from 'react-router-dom'
import CartLinePrice from './CartLinePrice'
import CartLineQtyWidget from './CartLineQtyWidget'
import { useTranslation } from 'react-i18next'
import FormattedPrice from '@components/FormattedPrice'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import { getProductUrl } from '../../urls'
import TuneVerticalIcon from '@icons/TuneVerticalIcon'
import DeleteIcon from '@icons/DeleteIcon'

// Kit contents block first widens, then (once that transition finishes) opens downward.
const KIT_CONTENTS_WIDTH_TRANSITION_MS = 150

type CartLine = CartLinesQueryResult['data']['cartLines']['results'][0]

interface CartLinesProps {
  lines: CartLine[]
  lineLoading: number | null
  isLoading: boolean
  readonly: boolean

  handleEditQty(linePk: number, qty: number, variantPk: number): Promise<void>

  handleRemoveLine(linePk: number, variantPk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  handleApplySupplyToAll?(supplyLineId: string): Promise<void>

  onClickProduct(specId: string | number, slug: string): void
}

interface CartLineRowProps {
  line: CartLine
  isLast: boolean
  lineLoading: number | null
  smUp: boolean
  readonly: boolean

  handleEditQty(linePk: number, qty: number, variantPk: number): Promise<void>

  handleRemoveLine(linePk: number, variantPk: number): Promise<void>

  handleEditLine(linePk: number): Promise<void>

  handleApplySupplyToAll?(supplyLineId: string): Promise<void>

  onClickProduct(specId: string | number, slug: string): void
}

const CartLineRow: React.FC<CartLineRowProps> = ({
  line,
  isLast,
  lineLoading,
  smUp,
  readonly,
  handleEditQty,
  handleRemoveLine,
  handleEditLine,
  handleApplySupplyToAll,
  onClickProduct,
}) => {
  const { t } = useTranslation()
  const [isKitContentsExpanded, setIsKitContentsExpanded] = useState(false)
  const [isKitContentsHeightOpen, setIsKitContentsHeightOpen] = useState(false)
  const widthTransitionTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const [fetchKitContents, kitContentsQuery] = useCartLineKitContentsLazyQuery({
    variables: { id: line.id },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const kitContentsParts = kitContentsQuery.data?.cartLine?.supplyParts

  useEffect(() => () => clearTimeout(widthTransitionTimeoutRef.current), [])

  const handleClickProduct = (specId: number, slug: string) => e => {
    e.preventDefault()
    onClickProduct(specId, slug)
  }

  const toggleKitContents = () => {
    clearTimeout(widthTransitionTimeoutRef.current)
    if (isKitContentsExpanded) {
      // Collapse height first; width shrinks once Collapse's exit transition finishes (see onExited below).
      setIsKitContentsHeightOpen(false)
    } else {
      // Widen first, then open downward once the width transition finishes.
      setIsKitContentsExpanded(true)
      if (!kitContentsQuery.called) {
        fetchKitContents()
      }
      widthTransitionTimeoutRef.current = setTimeout(() => {
        setIsKitContentsHeightOpen(true)
      }, KIT_CONTENTS_WIDTH_TRANSITION_MS)
    }
  }

  const variant = line.variant
  const product = variant.product
  const spec = product.siteSpecifics[0]
  const itemPrice = line.supplyParts.length
    ? line.supplyParts.reduce(
        (prev, curr) => {
          return {
            currency: curr.variant.wholesalePrice.currency,
            undiscount: prev.undiscount + curr.variant.wholesalePrice.undiscount * curr.quantity,
            discount: prev.discount + curr.variant.wholesalePrice.discount * curr.quantity,
          }
        },
        { currency: '', undiscount: 0, discount: 0 }
      )
    : {
        currency: line.variant.wholesalePrice.currency,
        undiscount: line.variant.wholesalePrice.undiscount,
        discount: line.variant.wholesalePrice.discount,
      }
  const isSale = itemPrice.undiscount !== itemPrice.discount

  const detailsLine = [
    `${t('sku', 'SKU')}: ${variant.sku}`,
    ...variant.properties.map(prop => `${prop.attributeName}: ${prop.valueName}`),
  ].join('  |  ')

  const kitContentsBlock = !!line.supplyParts.length && (
    <div
      className={`sm:mt-20 border border-gray-400 border-solid rounded-lg transition-all duration-150 ease-in-out ${
        isKitContentsExpanded ? 'bg-gray-50 w-full' : 'md:w-1/2'
      }`}
    >
      <div
        className='flex items-center justify-between px-10 py-8 cursor-pointer text-13'
        role='button'
        onClick={toggleKitContents}
      >
        <span className='font-bold'>{t('title.kitContents', 'KIT Contents')}</span>

        <span className='flex items-center text-gray-700'>
          <span className='mr-5'>
            {!isKitContentsExpanded &&
              t('componentsWithCount', {
                defaultValue: '{{count}} components',
                count: line.supplyParts.length,
              })}
          </span>
          {isKitContentsExpanded ? (
            <ExpandLessIcon fontSize='large' color='primary' />
          ) : (
            <ExpandMoreIcon fontSize='large' color='primary' />
          )}
        </span>
      </div>
      <Collapse
        in={isKitContentsHeightOpen}
        timeout='auto'
        unmountOnExit
        onExited={() => setIsKitContentsExpanded(false)}
      >
        <div className='px-10 pb-10 text-13'>
          <div className='text-gray-700 mb-5'>{t('description.includedInEachKit', 'Included in each kit')}</div>
          {kitContentsQuery.loading && !kitContentsParts ? (
            <Loader className='spinner spinner--small' type='ThreeDots' color='#333' visible height={60} width={60} />
          ) : (
            <div className='grid grid-cols-1 gap-x-20'>
              {(kitContentsParts || []).map((part, idx) => {
                const partSpec = part.variant?.product?.specifics[0]
                return (
                  <div key={idx} className='flex items-start justify-between py-3'>
                    <span className='flex items-start flex-1 min-w-0 mr-10'>
                      <CheckIcon className='text-green-light mr-5 mt-2' fontSize='small' />
                      <span className='break-words'>
                        <Link
                          to={getProductUrl(partSpec?.slug, partSpec?.pk)}
                          onClick={handleClickProduct(partSpec?.pk, partSpec?.slug)}
                          className='leading-6 no-underline hover:underline text-black'
                        >
                          {partSpec?.name || 'N/A'}
                        </Link>
                        <span>
                          &nbsp;
                          {t('xQtyPcs', {
                            defaultValue: 'x {{quantity}}pcs',
                            quantity: part.quantity,
                          })}
                        </span>
                      </span>
                    </span>
                    <span className='whitespace-nowrap flex-shrink-0'>
                      <CartLinePrice part={part} qty={1} />
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  )

  return (
    <li className='relative'>
      <Loader
        className='spinner spinner--small'
        type='ThreeDots'
        color='#333'
        visible={lineLoading === line.pk}
        height={100}
        width={100}
      />
      <div className='p-20 flex'>
        <Link
          to={getProductUrl(spec?.slug, spec?.pk)}
          onClick={handleClickProduct(spec?.pk, spec?.slug)}
          className={`mr-20 ${readonly ? 'w-60 min-w-60 sm:w-80 sm:min-w-80' : 'w-80 min-w-80 sm:w-120 sm:min-w-120'}`}
        >
          <img
            className='max-w-full shadow-thumbnail rounded-lg'
            src={variant.thumbnailUrl || spec?.thumbnailUrl}
            alt={spec?.name}
          />
        </Link>
        <div className='flex-1'>
          <div className='flex text-13 leading-18'>
            <div className='w-full flex-1'>
              <Link
                to={getProductUrl(spec?.slug, spec?.pk)}
                onClick={handleClickProduct(spec?.pk, spec?.slug)}
                className='no-underline leading-6 break-words text-black text-lg'
              >
                {spec?.shortName || spec?.name}
              </Link>
              {smUp && <div className='mt-10 break-words text-gray-700 uppercase'>{detailsLine}</div>}
              {!smUp && (
                <div className='mt-10 break-words text-base'>
                  <div className='my-10 uppercase'>
                    {t('sku', 'SKU')}:&nbsp;
                    {variant.sku}
                  </div>
                  {variant.properties.map(prop => (
                    <div className='my-10 uppercase'>
                      {prop.attributeName}:&nbsp;
                      {prop.valueName}
                    </div>
                  ))}
                  <div className='my-5 flex items-center justify-between'>
                    <span>{t('unitPrice', 'Unit price')}</span>
                    <span>
                      <span className={isSale ? 'line-through' : ''}>
                        <FormattedPrice amount={itemPrice.undiscount} currency={itemPrice.currency} />
                      </span>
                      {isSale && (
                        <span className='text-red'>
                          <FormattedPrice amount={itemPrice.discount} currency={itemPrice.currency} />
                        </span>
                      )}
                    </span>
                  </div>
                  <div className='my-15 flex items-center justify-between'>
                    <span>{t('quantity', 'Quantity')}</span>
                    <div>
                      {readonly ? (
                        <span>{line.quantity}</span>
                      ) : (
                        <CartLineQtyWidget
                          qty={line.quantity}
                          stockQty={variant.stockQuantity}
                          outOfStockStatus={variant.outOfStockStatus}
                          canEdit={variant.shippingRequired}
                          handleEditQty={qty => handleEditQty(line.pk, qty, line.variant.pk)}
                        />
                      )}
                    </div>
                  </div>
                  <div className='flex items-center justify-between my-20'>
                    <span className='text-xl'>{t('lineTotal', 'Line Total')}</span>
                    <span className='text-xl font-bold'>
                      <CartLinePrice line={line} qty={line.quantity} />
                    </span>
                  </div>
                </div>
              )}
            </div>
            {smUp && (
              <>
                <div className='ml-12 w-auto sm:w-90 text-center flex flex-col'>
                  <span className='text-gray mb-[6px]'>{t('unitPrice', 'Unit price')}</span>
                  <div className='h-30 flex flex-col items-center justify-center'>
                    <span className={isSale ? 'line-through' : ''}>
                      <FormattedPrice amount={itemPrice.undiscount} currency={itemPrice.currency} />
                    </span>
                    {isSale && (
                      <span className='text-red'>
                        <FormattedPrice amount={itemPrice.discount} currency={itemPrice.currency} />
                      </span>
                    )}
                  </div>
                </div>

                <div className='ml-12 w-100 text-center flex flex-col items-center'>
                  <span className='text-gray mb-[6px]'>{t('quantity', 'Quantity')}</span>
                  <div className='h-30 flex items-center justify-center'>
                    {readonly ? (
                      <span>{line.quantity}</span>
                    ) : (
                      <CartLineQtyWidget
                        qty={line.quantity}
                        stockQty={variant.stockQuantity}
                        outOfStockStatus={variant.outOfStockStatus}
                        canEdit={variant.shippingRequired}
                        handleEditQty={qty => handleEditQty(line.pk, qty, line.variant.pk)}
                      />
                    )}
                  </div>
                </div>
                <div className='ml-12 w-auto sm:w-90 text-right flex flex-col font-bold'>
                  <span className='text-gray mb-[6px] font-normal'>{t('lineTotal', 'Line Total')}</span>
                  <div className='h-30 flex flex-col items-end justify-center'>
                    <CartLinePrice line={line} qty={line.quantity} />
                  </div>
                </div>
              </>
            )}
          </div>

          {smUp && kitContentsBlock}
          {smUp && !readonly && (
            <div className='flex items-center justify-between gap-6 mt-15'>
              <div className='flex items-center gap-6 flex-1 min-w-0'>
                {line.variant.isSupply && line.variant.isCustomizable && (
                  <Button variant='outlined' onClick={() => handleEditLine(line.pk)} color='secondary' disableRipple>
                    <TuneVerticalIcon viewBox='0 -960 960 960' fontSize='medium' className='mr-5' />
                    <span className='normal-case'>{t('button.customizeKit', 'Customize KIT')}</span>
                  </Button>
                )}
                {line.variant.isSupply && handleApplySupplyToAll && (
                  <Button
                    className='transparent'
                    onClick={() => handleApplySupplyToAll(line.id)}
                    sx={{ marginLeft: line.variant.isCustomizable ? '0px' : '-10px' }}
                    color='secondary'
                    disableRipple
                  >
                    <span className='normal-case whitespace-nowrap'>
                      {t('button.applyThisSetupToAllKits', 'Apply this setup to all kits')}
                    </span>
                  </Button>
                )}
              </div>
              <IconButton
                className='flex-shrink-0 border border-solid border-gray-400 rounded-lg'
                onClick={() => handleRemoveLine(line.pk, line.variant.pk)}
                title={t('button.remove', 'Remove')}
              >
                <DeleteIcon viewBox='0 -960 960 960' color='warning' fontSize='small' />
              </IconButton>
            </div>
          )}
        </div>
      </div>
      {!smUp && kitContentsBlock && <div className='px-12'>{kitContentsBlock}</div>}
      {!smUp && !readonly && (
        <div className='flex items-center justify-between gap-6 mt-10 px-12 pb-12'>
          <div className='flex items-center gap-6 flex-1 min-w-0'>
            {line.variant.isSupply && (
              <>
                {line.variant.isCustomizable && (
                  <Button
                    variant='outlined'
                    size='medium'
                    startIcon={<TuneVerticalIcon viewBox='0 -960 960 960' />}
                    onClick={() => handleEditLine(line.pk)}
                    color='secondary'
                    disableRipple
                    className='flex-shrink-0'
                  >
                    <span className='normal-case text-sm'>{t('button.customizeKit', 'Customize KIT')}</span>
                  </Button>
                )}
                {handleApplySupplyToAll && (
                  <Button
                    size='small'
                    onClick={() => handleApplySupplyToAll(line.id)}
                    color='secondary'
                    disableRipple
                    className='transparent flex-1 min-w-0'
                  >
                    <span className='normal-case text-sm whitespace-normal leading-tight text-left'>
                      {t('button.applyThisSetupToAllKits', 'Apply this setup to all kits')}
                    </span>
                  </Button>
                )}
              </>
            )}
          </div>
          <IconButton
            className='flex-shrink-0 border border-solid border-gray-400 rounded-lg'
            onClick={() => handleRemoveLine(line.pk, line.variant.pk)}
            title={t('button.remove', 'Remove')}
          >
            <DeleteIcon viewBox='0 -960 960 960' color='warning' fontSize='small' />
          </IconButton>
        </div>
      )}
      {!isLast && <Divider sx={{ margin: '10px 20px', borderColor: '#f0f0f0' }} />}
    </li>
  )
}

const CartLines: React.FC<CartLinesProps> = ({
  lines,
  lineLoading,
  handleEditQty,
  handleRemoveLine,
  handleEditLine,
  handleApplySupplyToAll,
  onClickProduct,
  isLoading,
  readonly,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const smUp = useMediaQuery(theme.breakpoints.up('sm'))

  return (
    <>
      {!lines.length && (
        <div className='py-40 text-center text-lg relative'>
          {!isLoading && <span>{t('yourShoppingBagIsEmpty', 'Your Shopping Bag is empty.')}</span>}
        </div>
      )}
      <Loader
        className='spinner spinner--small'
        type='ThreeDots'
        color='#333'
        visible={isLoading}
        height={100}
        width={100}
      />
      <ul className='p-0 list-none'>
        {lines.map((line, idx) => (
          <CartLineRow
            key={line.id}
            line={line}
            isLast={idx === lines.length - 1}
            lineLoading={lineLoading}
            smUp={smUp}
            readonly={readonly}
            handleEditQty={handleEditQty}
            handleRemoveLine={handleRemoveLine}
            handleEditLine={handleEditLine}
            handleApplySupplyToAll={handleApplySupplyToAll}
            onClickProduct={onClickProduct}
          />
        ))}
      </ul>
    </>
  )
}

export default CartLines
